import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getEmployeeSalary,
  setEmployeeSalary,
  getEmployeeSalaryHistory,
  clearEmployeeSalary,
  getBulkSalaryRoster,
  saveBulkSalaries,
  type EmployeeSalaryInput,
  type BulkSalaryEntry,
} from '../services/api/employeeSalary';
import { invalidateMany } from './queryUtils';

/**
 * Phase 84 — TanStack Query hooks for the per-user/month salary record.
 *
 * Every mutation invalidates the freelancer-invoice queries as well as the
 * salary itself: changing a salary changes the invoice figure downstream, and
 * leaving a stale invoice on screen after a correction is exactly the kind of
 * money-looks-wrong bug this phase exists to prevent.
 */

function salaryKey(userId: number | undefined, year: number, month: number) {
  return ['employeeSalary', userId, year, month] as const;
}

// quick-260813-hob: callers on non-finance surfaces pass `enabled: false` so
// the query never fires and never 403s into the console. `?? true` keeps the
// existing single call site's behaviour unchanged when it omits `opts`.
export function useEmployeeSalary(
  userId: number | undefined,
  year: number,
  month: number,
  opts?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: salaryKey(userId, year, month),
    queryFn: () => getEmployeeSalary(userId!, year, month),
    enabled: (opts?.enabled ?? true) && !!userId,
  });
}

/**
 * The edit trail (D-03). `enabled` gates the fetch so it only runs when the
 * history toggle is opened — same shape as `useEmploymentRelationships`'s
 * history option, so the two sections on the user-detail page behave alike.
 */
export function useEmployeeSalaryHistory(
  userId: number | undefined,
  year: number,
  month: number,
  opts?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [...salaryKey(userId, year, month), 'history'],
    queryFn: () => getEmployeeSalaryHistory(userId!, year, month),
    enabled: (opts?.enabled ?? true) && !!userId,
  });
}

function invalidateSalaryAndInvoices(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: number | undefined
): void {
  invalidateMany(queryClient, [
    ['employeeSalary', userId],
    ['freelancerInvoices'],
    ['employeeInvoices', userId],
  ]);
}

export function useSetEmployeeSalary(userId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { year: number; month: number; input: EmployeeSalaryInput }) =>
      setEmployeeSalary(userId!, vars.year, vars.month, vars.input),
    onSuccess: () => invalidateSalaryAndInvoices(queryClient, userId),
  });
}

/** 84-05 — the bulk roster for one month, with the previous month attached. */
export function useBulkSalaryRoster(year: number, month: number) {
  return useQuery({
    queryKey: ['bulkSalaryRoster', year, month],
    queryFn: () => getBulkSalaryRoster(year, month),
  });
}

export function useSaveBulkSalaries(year: number, month: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entries: BulkSalaryEntry[]) => saveBulkSalaries(year, month, entries),
    onSuccess: () => {
      invalidateMany(queryClient, [
        ['bulkSalaryRoster', year, month],
        ['employeeSalary'],
        ['freelancerInvoices'],
        ['employeeInvoices'],
      ]);
    },
  });
}

export function useClearEmployeeSalary(userId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { year: number; month: number }) =>
      clearEmployeeSalary(userId!, vars.year, vars.month),
    onSuccess: () => invalidateSalaryAndInvoices(queryClient, userId),
  });
}
